import React from 'react';
import { useFirebase } from '../services/firebase';
import { LOADING_STATE } from '../util/loading';
import {
  getUserChannelPreferences,
  setUserChannelPreferences,
  UserChannelPreferences,
} from '../services/buildfire/rdb/user';
import { getDatabase } from 'firebase/database';

function useUserChannelPreferences({
  userId,
  templateIds,
}: {
  userId?: string;
  templateIds: string[];
}) {
  const { app } = useFirebase();
  const [state, setState] = React.useState<{
    channelPreferences: { [templateId: string]: UserChannelPreferences };
    loadingState: LOADING_STATE;
  }>({
    channelPreferences: {},
    loadingState: LOADING_STATE.LOADING,
  });

  const updateUserChannelPreference = React.useCallback(
    async (
      userId: string,
      templateId: string,
      channel: 'email' | 'sms' | 'push',
      enabled: boolean
    ) => {
      const db = getDatabase(app);
      await setUserChannelPreferences({
        database: db,
        userId,
        courierTemplateId: templateId,
        channel,
        enabled,
      });
      setState((state) => ({
        ...state,
        channelPreferences: {
          ...state.channelPreferences,
          [templateId]: {
            ...state.channelPreferences[templateId],
            [channel]: enabled,
          },
        },
        loadingState: LOADING_STATE.LOADED,
      }));
    },
    []
  );

  React.useEffect(() => {
    async function fetchPreferences(userId: string) {
      setState((state) => ({ ...state, loadingState: LOADING_STATE.LOADING }));
      try {
        const db = getDatabase(app);
        for (const templateId of templateIds) {
          const channelPreferences = await getUserChannelPreferences({
            database: db,
            userId,
            courierTemplateId: templateId,
          });
          setState((state) => ({
            ...state,
            channelPreferences: {
              ...state.channelPreferences,
              [templateId]: channelPreferences,
            },
          }));
        }
        setState((state) => ({
          ...state,
          loadingState: LOADING_STATE.LOADED,
        }));
      } catch (e) {
        console.error('Error fetching user channel preferences:', e);
        setState((state) => ({ ...state, loadingState: LOADING_STATE.ERROR }));
        return;
      }
    }

    if (userId) {
      fetchPreferences(userId);
    } else {
      // clear state if logged out
      setState({
        channelPreferences: {},
        loadingState: LOADING_STATE.LOADED,
      });
    }
  }, [userId, JSON.stringify(templateIds)]);

  return {
    channelPreferences: state.channelPreferences,
    loadingState: state.loadingState,
    updateUserChannelPreference,
  };
}

export default useUserChannelPreferences;
